import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { B2bProduct } from "@/types/b2b";

const mapRow = (r: any): B2bProduct => ({
  slug: r.slug,
  title: r.title,
  tagline: r.tagline ?? "",
  heroHeadline: r.hero_headline ?? "",
  heroSubheadline: r.hero_subheadline ?? "",
  heroMetrics: r.hero_metrics ?? [],
  problemIntro: r.problem_intro ?? "",
  problemQuote: r.problem_quote ?? "",
  problemCards: r.problem_cards ?? [],
  solutionIntro: r.solution_intro ?? "",
  pillars: r.pillars ?? [],
  timeline: r.timeline ?? [],
  resultIntro: r.result_intro ?? "",
  resultMetrics: r.result_metrics ?? [],
  resultOutro: r.result_outro ?? "",
  plans: r.plans ?? [],
  guaranteeTitle: r.guarantee_title ?? "",
  guaranteeText: r.guarantee_text ?? "",
  faq: r.faq ?? [],
  aboutText: r.about_text ?? "",
  ctaHeadline: r.cta_headline ?? "",
  ctaSubtext: r.cta_subtext ?? "",
  ctaButtonLabel: r.cta_button_label ?? "Agendar diagnóstico",
  ctaNote: r.cta_note ?? "",
  coverImage: r.cover_image ?? undefined,
  published: !!r.published,
  featured: !!r.featured,
});

const toRow = (p: Partial<B2bProduct>) => {
  const r: any = {};
  if ("slug" in p) r.slug = p.slug;
  if ("title" in p) r.title = p.title;
  if ("tagline" in p) r.tagline = p.tagline ?? "";
  if ("heroHeadline" in p) r.hero_headline = p.heroHeadline ?? "";
  if ("heroSubheadline" in p) r.hero_subheadline = p.heroSubheadline ?? "";
  if ("heroMetrics" in p) r.hero_metrics = p.heroMetrics ?? [];
  if ("problemIntro" in p) r.problem_intro = p.problemIntro ?? "";
  if ("problemQuote" in p) r.problem_quote = p.problemQuote ?? "";
  if ("problemCards" in p) r.problem_cards = p.problemCards ?? [];
  if ("solutionIntro" in p) r.solution_intro = p.solutionIntro ?? "";
  if ("pillars" in p) r.pillars = p.pillars ?? [];
  if ("timeline" in p) r.timeline = p.timeline ?? [];
  if ("resultIntro" in p) r.result_intro = p.resultIntro ?? "";
  if ("resultMetrics" in p) r.result_metrics = p.resultMetrics ?? [];
  if ("resultOutro" in p) r.result_outro = p.resultOutro ?? "";
  if ("plans" in p) r.plans = p.plans ?? [];
  if ("guaranteeTitle" in p) r.guarantee_title = p.guaranteeTitle ?? "";
  if ("guaranteeText" in p) r.guarantee_text = p.guaranteeText ?? "";
  if ("faq" in p) r.faq = p.faq ?? [];
  if ("aboutText" in p) r.about_text = p.aboutText ?? "";
  if ("ctaHeadline" in p) r.cta_headline = p.ctaHeadline ?? "";
  if ("ctaSubtext" in p) r.cta_subtext = p.ctaSubtext ?? "";
  if ("ctaButtonLabel" in p) r.cta_button_label = p.ctaButtonLabel ?? "Agendar diagnóstico";
  if ("ctaNote" in p) r.cta_note = p.ctaNote ?? "";
  if ("coverImage" in p) r.cover_image = p.coverImage || null;
  if ("published" in p) r.published = !!p.published;
  if ("featured" in p) r.featured = !!p.featured;
  return r;
};

export function useB2bProductsQuery(opts: { adminMode?: boolean } = {}) {
  const qc = useQueryClient();
  const key = ["b2b_products", opts.adminMode ? "admin" : "public"];

  const query = useQuery({
    queryKey: key,
    queryFn: async () => {
      let q = supabase.from("b2b_products" as any).select("*").order("created_at", { ascending: false });
      if (!opts.adminMode) q = q.eq("published", true);
      const { data, error } = await q;
      if (error) throw error;
      return ((data as any[]) ?? []).map(mapRow);
    },
  });

  const add = useMutation({
    mutationFn: async (p: B2bProduct) => {
      const { error } = await supabase.from("b2b_products" as any).insert(toRow(p));
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["b2b_products"] }),
  });

  const update = useMutation({
    mutationFn: async ({ slug, data }: { slug: string; data: Partial<B2bProduct> }) => {
      const { error } = await supabase.from("b2b_products" as any).update(toRow(data)).eq("slug", slug);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["b2b_products"] }),
  });

  const remove = useMutation({
    mutationFn: async (slug: string) => {
      const { error } = await supabase.from("b2b_products" as any).delete().eq("slug", slug);
      if (error) throw error;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ["b2b_products"] }),
  });

  return {
    products: query.data ?? [],
    isLoading: query.isLoading,
    error: query.error,
    add: add.mutateAsync,
    update: (slug: string, data: Partial<B2bProduct>) => update.mutateAsync({ slug, data }),
    remove: remove.mutateAsync,
  };
}
